import type { LoggingFlowPayload, LoggingFlowReviewItem } from "../lib/logging-flow";
import type { NewPlateItem, PlateItemSourceRef } from "../lib/plate";

// Food Library pure helpers (plan todo 19). This module is the testable seam
// for the Library screen: row -> review-item / plate-item mapping, quick
// action derivation, local search filtering, the two-step delete reducer, and
// the optimistic row transforms used by the wired screen's
// .withOptimisticUpdate callbacks. Unknown nutrition stays undefined and is
// NEVER zero-filled. RELATIVE imports only (the "@/" alias breaks vitest).

/** Minimal structural view of a foodEntries row (recent/frequent/yesterday). */
export interface LibraryEntry {
  readonly _id: string;
  readonly name: string;
  readonly quantity?: number;
  readonly unit?: string;
  readonly estimatedKcal?: number;
  readonly finalKcal?: number;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
  readonly mealType?: string;
  readonly confidence?: string;
  readonly dataSource?: string;
  readonly kcalUnknown?: boolean;
  readonly status?: string;
  readonly loggedAt?: number;
}

export interface FrequentFoodView extends LibraryEntry {
  readonly count?: number;
}

export interface SavedMealView {
  readonly _id: string;
  readonly name: string;
  readonly mealType?: string;
  readonly itemsJson: string;
  readonly totalCalories?: number;
  readonly favorite: boolean;
  readonly timesUsed: number;
  readonly lastUsedAt: number;
}

export interface CustomFoodView {
  readonly _id: string;
  readonly name: string;
  readonly brand?: string;
  readonly servingUnit: string;
  readonly servingWeightG?: number;
  readonly calories?: number;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
  readonly additionalNutrientsJson?: string;
  readonly barcode?: string;
  readonly source: string;
  readonly favorite: boolean;
  readonly timesUsed: number;
}

export interface RecipeIngredientView {
  readonly name: string;
  readonly quantity?: number;
  readonly unit?: string;
  readonly calories?: number;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
}

export interface RecipeTotalsView {
  readonly calories?: number;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
  readonly complete: boolean;
}

export interface RecipeView {
  readonly _id: string;
  readonly name: string;
  readonly ingredients: readonly RecipeIngredientView[];
  readonly servings?: number;
  readonly totalCookedWeightG?: number;
  readonly favorite: boolean;
  readonly timesUsed: number;
  readonly totals?: RecipeTotalsView;
}

export interface MemoryView {
  readonly _id: string;
  readonly foodName: string;
  readonly servingSize?: string;
  readonly calories: number;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
  readonly notes?: string;
  readonly confidence: string;
  readonly source: string;
  readonly favorite?: boolean;
  readonly timesUsed: number;
  readonly lastUsedAt: number;
}

export type FavoriteKind = "savedMeal" | "customFood" | "recipe" | "foodMemory";

export interface FavoriteView {
  readonly kind: FavoriteKind;
  readonly refId: string;
  readonly name: string;
  readonly calories?: number;
  readonly servingLabel?: string;
}

export interface FavoriteToggleTarget {
  readonly kind: FavoriteKind;
  readonly id: string;
  readonly name: string;
  readonly favorite: boolean;
  readonly calories?: number;
  readonly servingLabel?: string;
}

export interface DeleteTarget {
  readonly kind: FavoriteKind;
  readonly id: string;
  readonly name: string;
}

// ── Unknown-preserving nutrition mapping ────────────────────────────────────

/** Resolved kcal for an entry-like row; undefined stays undefined. */
export function entryKcal(entry: {
  readonly estimatedKcal?: number;
  readonly finalKcal?: number;
  readonly kcalUnknown?: boolean;
}): number | undefined {
  if (entry.kcalUnknown === true) return undefined;
  return entry.finalKcal ?? entry.estimatedKcal;
}

function spreadDefined<T extends Record<string, number | string | undefined>>(
  source: T,
): Partial<T> {
  const out: Partial<T> = {};
  for (const [key, value] of Object.entries(source)) {
    if (value !== undefined) {
      (out as Record<string, unknown>)[key] = value;
    }
  }
  return out;
}

export function entryToReviewItem(entry: LibraryEntry): LoggingFlowReviewItem {
  return {
    name: entry.name,
    ...spreadDefined({
      quantity: entry.quantity,
      unit: entry.unit,
      calories: entryKcal(entry),
      proteinG: entry.proteinG,
      carbsG: entry.carbsG,
      fatG: entry.fatG,
      fiberG: entry.fiberG,
      dataSource: entry.dataSource,
      confidence: entry.confidence,
    }),
  };
}

/** Defensive itemsJson parse — the persisted snapshot is an untrusted boundary. */
export function parseSavedMealItems(itemsJson: string): LibraryEntry[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(itemsJson);
  } catch {
    console.warn("[Frontend][Library] Saved meal itemsJson is corrupt; treating as empty");
    return [];
  }
  if (!Array.isArray(parsed)) return [];
  const items: LibraryEntry[] = [];
  for (const raw of parsed) {
    if (typeof raw !== "object" || raw === null) continue;
    const item = raw as Record<string, unknown>;
    if (typeof item.name !== "string" || item.name.trim().length === 0) continue;
    items.push({
      _id: `saved_item_${items.length}`,
      name: item.name,
      ...(spreadDefined({
        quantity: typeof item.quantity === "number" ? item.quantity : undefined,
        unit: typeof item.unit === "string" ? item.unit : undefined,
        estimatedKcal: typeof item.estimatedKcal === "number" ? item.estimatedKcal : undefined,
        finalKcal: typeof item.finalKcal === "number" ? item.finalKcal : undefined,
        proteinG: typeof item.proteinG === "number" ? item.proteinG : undefined,
        carbsG: typeof item.carbsG === "number" ? item.carbsG : undefined,
        fatG: typeof item.fatG === "number" ? item.fatG : undefined,
        fiberG: typeof item.fiberG === "number" ? item.fiberG : undefined,
        dataSource: typeof item.dataSource === "string" ? item.dataSource : undefined,
        confidence: typeof item.confidence === "string" ? item.confidence : undefined,
      }) as Partial<LibraryEntry>),
      kcalUnknown: item.kcalUnknown === true ? true : undefined,
    });
  }
  return items;
}

export function savedMealToReviewItems(meal: SavedMealView): LoggingFlowReviewItem[] {
  return parseSavedMealItems(meal.itemsJson).map(entryToReviewItem);
}

/** Minimal memory shape needed for review mapping (FoodMemoryLike-compatible). */
export interface MemoryNutritionLike {
  readonly foodName: string;
  readonly servingSize?: string;
  readonly calories: number;
  readonly proteinG?: number;
  readonly carbsG?: number;
  readonly fatG?: number;
  readonly fiberG?: number;
  readonly confidence: string;
}

export function memoryToReviewItem(memory: MemoryNutritionLike): LoggingFlowReviewItem {
  return {
    name: memory.foodName,
    ...spreadDefined({
      unit: memory.servingSize,
      calories: memory.calories,
      proteinG: memory.proteinG,
      carbsG: memory.carbsG,
      fatG: memory.fatG,
      fiberG: memory.fiberG,
      confidence: memory.confidence,
    }),
    dataSource: "memory",
  };
}

export function customFoodDataSource(source: string): string | undefined {
  const map: Record<string, string> = {
    manual: "user-entered",
    label: "nutrition-label",
    barcode: "barcode",
  };
  return map[source];
}

export function customFoodToReviewItem(food: CustomFoodView): LoggingFlowReviewItem {
  return {
    name: food.name,
    quantity: 1,
    unit: food.servingUnit,
    ...spreadDefined({
      calories: food.calories,
      proteinG: food.proteinG,
      carbsG: food.carbsG,
      fatG: food.fatG,
      fiberG: food.fiberG,
      dataSource: customFoodDataSource(food.source),
    }),
  };
}

/** Per-serving nutrition; a nutrient is defined only when the total is. */
export function recipePerServing(recipe: RecipeView): RecipeTotalsView | undefined {
  const totals = recipe.totals;
  if (totals === undefined) return undefined;
  const servings = recipe.servings;
  if (servings === undefined || servings <= 0) return undefined;
  const divide = (value: number | undefined) =>
    value === undefined ? undefined : value / servings;
  return {
    calories: divide(totals.calories),
    proteinG: divide(totals.proteinG),
    carbsG: divide(totals.carbsG),
    fatG: divide(totals.fatG),
    fiberG: divide(totals.fiberG),
    complete: totals.complete,
  };
}

export function recipeToReviewItem(recipe: RecipeView): LoggingFlowReviewItem {
  const perServing = recipePerServing(recipe);
  const complete = recipe.totals?.complete === true;
  return {
    name: recipe.name,
    quantity: 1,
    unit: "serving",
    ...spreadDefined({
      calories: perServing?.calories,
      proteinG: perServing?.proteinG,
      carbsG: perServing?.carbsG,
      fatG: perServing?.fatG,
      fiberG: perServing?.fiberG,
    }),
    dataSource: complete ? "recipe" : "incomplete",
    confidence: complete ? "high" : "low",
  };
}

export function favoriteToReviewItem(favorite: FavoriteView): LoggingFlowReviewItem {
  return {
    name: favorite.name,
    ...spreadDefined({
      unit: favorite.servingLabel,
      calories: favorite.calories,
    }),
    dataSource: favorite.kind === "foodMemory" ? "memory" : "user-entered",
  };
}

/** Review item -> plate item. Plate requires a dataSource string. */
export function reviewItemToPlateItem(
  item: LoggingFlowReviewItem,
  sourceRef?: PlateItemSourceRef,
): NewPlateItem {
  return {
    name: item.name,
    ...spreadDefined({
      quantity: item.quantity,
      unit: item.unit,
      calories: item.calories,
      proteinG: item.proteinG,
      carbsG: item.carbsG,
      fatG: item.fatG,
      fiberG: item.fiberG,
      confidence: item.confidence,
    }),
    dataSource: item.dataSource ?? "user-entered",
    ...(sourceRef === undefined ? {} : { sourceRef }),
  };
}

// ── Review flow starter (PREFILL ONLY — never persists) ─────────────────────

export type StartFlowFn = (kind: "review", payload?: LoggingFlowPayload) => void;

/**
 * Every Library "Log again" / quick action routes through here: the Meal
 * Review flow is prefilled with the items and NOTHING persists until the
 * user presses Log meal in the review screen (review-before-save invariant).
 * Returns false (and warns) when called with no items so callers can hide
 * instead of firing empty flows.
 */
export function startReviewWithItems(
  startFlow: StartFlowFn,
  items: readonly LoggingFlowReviewItem[],
): boolean {
  if (items.length === 0) {
    console.warn("[Frontend][Library] Refusing to prefill review with zero items");
    return false;
  }
  console.log("[Frontend][Library] Prefilling meal review", {
    itemCount: items.length,
    source: "recent",
  });
  startFlow("review", { items, source: "recent" });
  return true;
}

// ── Quick actions ───────────────────────────────────────────────────────────

export function loggedEntries(entries: readonly LibraryEntry[]): LibraryEntry[] {
  return entries.filter((entry) => entry.status === undefined || entry.status === "logged");
}

export function mealTypeItems(
  entries: readonly LibraryEntry[],
  mealType: string,
): LoggingFlowReviewItem[] {
  return loggedEntries(entries)
    .filter((entry) => entry.mealType === mealType)
    .map(entryToReviewItem);
}

export interface CopyMealOption {
  readonly mealType: string;
  readonly items: readonly LoggingFlowReviewItem[];
}

const MEAL_TYPE_ORDER: readonly string[] = ["breakfast", "lunch", "dinner", "snack"];

/** Yesterday's logged meals grouped by meal type for the Copy-a-meal sheet. */
export function groupLoggedMeals(entries: readonly LibraryEntry[]): CopyMealOption[] {
  const logged = loggedEntries(entries);
  const groups = new Map<string, LoggingFlowReviewItem[]>();
  for (const entry of logged) {
    const key = entry.mealType ?? "other";
    const bucket = groups.get(key) ?? [];
    bucket.push(entryToReviewItem(entry));
    groups.set(key, bucket);
  }
  return [...groups.entries()]
    .map(([mealType, items]) => ({ mealType, items }))
    .sort((left, right) => {
      const li = MEAL_TYPE_ORDER.indexOf(left.mealType);
      const ri = MEAL_TYPE_ORDER.indexOf(right.mealType);
      return (li === -1 ? MEAL_TYPE_ORDER.length : li) - (ri === -1 ? MEAL_TYPE_ORDER.length : ri);
    });
}

/**
 * "Usual lunch": the most frequent items logged with mealType lunch. The
 * backend aggregates over a 60-day window (superset of the spec's 30 days —
 * documented in the task learnings). Top 3 by logged count.
 */
export function buildUsualLunchItems(
  frequent: readonly FrequentFoodView[],
): LoggingFlowReviewItem[] {
  return frequent
    .filter((food) => food.mealType === "lunch")
    .sort((left, right) => (right.count ?? 0) - (left.count ?? 0))
    .slice(0, 3)
    .map(entryToReviewItem);
}

export interface QuickActionAvailability {
  readonly repeatBreakfast: boolean;
  readonly copyMeal: boolean;
  readonly usualLunch: boolean;
}

/** Quick actions are HIDDEN (never disabled) until their data exists. */
export function quickActionAvailability(input: {
  readonly yesterdayEntries: readonly LibraryEntry[] | undefined;
  readonly frequentFoods: readonly FrequentFoodView[] | undefined;
}): QuickActionAvailability {
  const yesterday = input.yesterdayEntries ?? [];
  const frequent = input.frequentFoods ?? [];
  return {
    repeatBreakfast: mealTypeItems(yesterday, "breakfast").length > 0,
    copyMeal: loggedEntries(yesterday).length > 0,
    usualLunch: buildUsualLunchItems(frequent).length > 0,
  };
}

export function anyQuickActionAvailable(availability: QuickActionAvailability): boolean {
  return availability.repeatBreakfast || availability.copyMeal || availability.usualLunch;
}

// ── Local search filtering (300ms debounce lives in the wired screen) ───────

export function matchesQuery(name: string, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return true;
  return name.toLowerCase().includes(needle);
}

export function filterRows<T>(
  rows: readonly T[],
  query: string,
  nameOf: (row: T) => string,
): T[] {
  if (query.trim().length === 0) return [...rows];
  return rows.filter((row) => matchesQuery(nameOf(row), query));
}

// ── Two-step delete confirm ─────────────────────────────────────────────────

export type DeleteConfirmState = "idle" | "confirming";
export type DeleteConfirmAction = "request" | "cancel" | "close";

export function deleteConfirmReducer(
  _state: DeleteConfirmState,
  action: DeleteConfirmAction,
): DeleteConfirmState {
  switch (action) {
    case "request":
      return "confirming";
    case "cancel":
    case "close":
      return "idle";
  }
}

// ── Optimistic row transforms (used by .withOptimisticUpdate callbacks) ─────

export function patchRowById<T extends { readonly _id: string }>(
  rows: readonly T[],
  id: string,
  patch: Partial<T>,
): T[] {
  return rows.map((row) => (row._id === id ? { ...row, ...patch } : row));
}

export function removeRowById<T extends { readonly _id: string }>(
  rows: readonly T[],
  id: string,
): T[] {
  return rows.filter((row) => row._id !== id);
}

export function toggleFavoriteInRows<T extends { readonly _id: string; readonly favorite?: boolean }>(
  rows: readonly T[],
  id: string,
): T[] {
  return rows.map((row) =>
    row._id === id ? { ...row, favorite: !(row.favorite ?? false) } : row,
  );
}

/** Insert (favorited) or remove (unfavorited) a row in listFavorites. */
export function upsertFavoriteView(
  favorites: readonly FavoriteView[],
  item: FavoriteView,
  favorited: boolean,
): FavoriteView[] {
  const rest = favorites.filter(
    (fav) => !(fav.kind === item.kind && fav.refId === item.refId),
  );
  return favorited ? [item, ...rest] : rest;
}

export function favoriteViewFrom(target: FavoriteToggleTarget): FavoriteView {
  return {
    kind: target.kind,
    refId: target.id,
    name: target.name,
    ...(target.calories === undefined ? {} : { calories: target.calories }),
    ...(target.servingLabel === undefined ? {} : { servingLabel: target.servingLabel }),
  };
}

/** Display helper: kcal label with an em-dash for unknown values. */
export function kcalLabel(calories: number | undefined): string {
  return calories === undefined ? "—" : `${Math.round(calories)} kcal`;
}
